import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import EmploymentTypeFilter from './components/EmploymentTypeFilter';
import CitizenshipFilter from './components/CitizenshipFilter';
import TimePeriodSelector from './components/TimePeriodSelector';
import KPICard from './components/KPICard';
import EmploymentDistributionChart from './components/EmploymentDistributionChart';
import CitizenshipDistributionChart from './components/CitizenshipDistributionChart';
import DemographicBreakdownTable from './components/DemographicBreakdownTable';
import BookmarkManager from './components/BookmarkManager';
import ExportOptions from './components/ExportOptions';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import EmptyStatePrompt from '../../components/EmptyStatePrompt';
import { employeeService } from '../../services/employeeService';
import { workforceAnalyticsService } from '../../services/workforceAnalyticsService';
import TeaserEditModal from '../../components/TeaserEditModal';
import { useTeaserEdit } from '../../hooks/useTeaserEdit';
import { billingService } from '../../services/billingService';
import { supabase } from '../../lib/supabase';


const WorkforceAnalytics = () => {
  const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState(['permanent', 'contract', 'casual', 'temporary']);
  const [selectedCitizenship, setSelectedCitizenship] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('q1-2026');
  const [globalFilters, setGlobalFilters] = useState({});
  const { companySettings } = useCompanySettings();

  const companyName = companySettings?.companyName || 'HR Analytics';

  useEffect(() => {
    document.title = `Workforce Analytics - ${companyName}`;
  }, [companyName]);

  const handleGlobalFilterChange = (filters) => {
    setGlobalFilters(filters);
  };

  const handleLoadBookmark = (filters) => {
    if (filters?.employmentTypes) {
      setSelectedEmploymentTypes(filters?.employmentTypes);
    }
    if (filters?.citizenship) {
      setSelectedCitizenship(filters?.citizenship);
    }
    if (filters?.period) {
      setSelectedPeriod(filters?.period);
    }
  };

  const currentFilters = {
    employmentTypes: selectedEmploymentTypes,
    citizenship: selectedCitizenship,
    period: selectedPeriod
  };

  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('active');

  const { 
    editingField: teaserEditField, 
    showRedirectModal, 
    startEdit, 
    handleInput: handleTeaserInput, 
    closeModal,
    isEditable 
  } = useTeaserEdit(subscriptionStatus === 'active');

  useEffect(() => {
    loadData();
    checkSubscription();
  }, [selectedPeriod, selectedEmploymentTypes, selectedCitizenship]);

  const loadData = async () => {
    try {
      setLoading(true);
      const employeesData = await employeeService?.getAll();
      setEmployees(employeesData || []);

      if (employeesData?.length > 0) {
        const analytics = await workforceAnalyticsService?.getAnalytics({
          period: selectedPeriod,
          employmentType: selectedEmploymentTypes,
          citizenship: selectedCitizenship
        });
        setAnalyticsData(analytics);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkSubscription = async () => {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (user?.user?.id) {
        const { data: companySettings } = await supabase?.from('company_settings')?.select('id')?.eq('user_id', user?.user?.id)?.single();
        
        if (companySettings?.id) {
          const subscriptionDetails = await billingService?.getSubscriptionDetails(companySettings?.id);
          const status = subscriptionDetails?.subscriptionStatus || 'suspended';
          setSubscriptionStatus(status);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleAddEmployees = () => {
    window.location.href = '/workforce-management-dashboard';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no employees
  if (!employees || employees?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
              Workforce Analytics
            </h1>
            <p className="caption text-sm md:text-base text-muted-foreground">
              Comprehensive insights into your workforce composition and trends
            </p>
          </div>

          <div className="bg-card rounded-lg border border-border p-8">
            <EmptyStatePrompt
              icon="BarChart"
              title="No Workforce Data Available"
              description="Add employees to your workforce to unlock powerful analytics including demographic breakdowns, employment type distributions, citizenship analysis, and departmental staffing insights."
              actionLabel="Add Employees"
              onAction={handleAddEmployees}
            />
          </div>
        </main>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Workforce Analytics - {companyName}</title>
        <meta name="description" content="Strategic workforce insights and employment pattern analysis for PNG organizations with real-time demographic visualization and compliance tracking" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <ComplianceAlertBanner />
        <LegalDisclaimerBanner />
        <GlobalFilterBar onFilterChange={handleGlobalFilterChange} />
        <DataRefreshIndicator />

        <main className="pt-32 pb-12 px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 2xl:px-16">
          <div className="max-w-[1920px] mx-auto w-full">
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-2 md:mb-3 break-words leading-tight">
                Workforce Analytics Dashboard
              </h1>
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground break-words leading-relaxed max-w-4xl">
                Strategic workforce insights and employment pattern analysis across PNG operations with real-time demographic visualization and compliance tracking
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 md:gap-6 mb-6 md:mb-8">
              <div className="lg:col-span-3 w-full">
                <EmploymentTypeFilter
                  selectedTypes={selectedEmploymentTypes}
                  onChange={setSelectedEmploymentTypes}
                />
              </div>
              <div className="lg:col-span-3 w-full">
                <CitizenshipFilter
                  value={selectedCitizenship}
                  onChange={setSelectedCitizenship}
                />
              </div>
              <div className="lg:col-span-6 w-full">
                <TimePeriodSelector
                  selectedPeriod={selectedPeriod}
                  onChange={setSelectedPeriod}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              <KPICard
                title="Total Headcount"
                value="527"
                change="+4.2%"
                changeType="positive"
                icon="Users"
                iconColor="var(--color-primary)"
              />
              <KPICard
                title="Employment Distribution"
                value="44.4%"
                change="+1.8%"
                changeType="positive"
                icon="PieChart"
                iconColor="var(--color-accent)"
              />
              <KPICard
                title="Turnover Rate"
                value="8.3%"
                change="-2.1%"
                changeType="positive"
                icon="TrendingDown"
                iconColor="var(--color-success)"
              />
              <KPICard
                title="Cost Per Hire"
                value="K12,450"
                change="+5.6%"
                changeType="negative"
                icon="DollarSign"
                iconColor="var(--color-warning)"
              />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 md:gap-6 mb-6 md:mb-8">
              <div className="xl:col-span-8 w-full">
                <EmploymentDistributionChart />
              </div>
              <div className="xl:col-span-4 w-full">
                <CitizenshipDistributionChart />
              </div>
            </div>

            <div className="mb-6 md:mb-8">
              <DemographicBreakdownTable 
                selectedEmploymentTypes={selectedEmploymentTypes}
                selectedCitizenship={selectedCitizenship}
              />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
              <BookmarkManager
                currentFilters={currentFilters}
                onLoadBookmark={handleLoadBookmark}
              />
              <ExportOptions />
            </div>
          </div>
        </main>

        <footer className="bg-card border-t border-border py-6 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <p className="caption text-sm text-muted-foreground">
              &copy; {new Date()?.getFullYear()} {companyName}. All rights reserved. | Compliant with PNG Employment Act 1978
            </p>
          </div>
        </footer>
      </div>
      <TeaserEditModal isOpen={showRedirectModal} onClose={closeModal} />
    </React.Fragment>
  );
};

export default WorkforceAnalytics;